import type {
    CompanyPolicyDetailDTO,
    CreateCompanyPolicyDTO,
    PolicyType,
    UpdateCompanyPolicyDTO,
} from '@/modules/hr/company-policy/domain/types';
import { usePermissions } from '@/modules/auth/presentation/model/usePermissions';
import { Permissions } from '@/modules/roles/domain/contracts/permissions';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    confirm,
    toast,
    FormInput,
    FormRoot,
    FormTextarea,
    Modal,
    zodResolver,
} from '@devhop/ui';
import { FormTipTapEditor } from '@/shared/ui/FormTipTapEditor';
import { PlusIcon, Settings, Sparkles } from 'lucide-react';
import * as RHF from 'react-hook-form';
import { useMemo, useState } from 'react';
import { SimpleSelect } from '@/shared/ui/SimpleSelect';
import { useDisclosure } from '@/shared/hooks/useDisclosure';
import {
    useCreatePolicyTypeMutation,
    useDeletePolicyTypeMutation,
    usePolicyTypesQuery,
    useUpdatePolicyTypeMutation,
} from '../api/queries';
import { z } from 'zod';
import { policyFormSamples } from './policyFormSamples';
import { PolicyTypeForm } from './PolicyTypeForm';

const POLICY_TYPE_NONE = '__none__';

const PolicyFormSchema = z.object({
    title: z.string().min(1, 'ກະລຸນາປ້ອນຊື່ນະໂຍບາຍ'),
    code: z.string(),
    description: z.string(),
    policyTypeId: z.string(),
    effectiveDate: z.string().min(1, 'ກະລຸນາເລືອກວັນທີມີຜົນບັງຄັບໃຊ້'),
    content: z.string(),
});

export type PolicyFormValues = {
    title: string;
    code: string;
    description: string;
    policyTypeId: string;
    effectiveDate: string;
    content: string;
};

export type PolicyFormProps =
    | {
          mode: 'create';
          policy?: CompanyPolicyDetailDTO | null;
          /** When duplicating, prefill form from this policy (title gets " (ສຳເນົາ)" appended) */
          duplicateFrom?: CompanyPolicyDetailDTO | null;
          onSubmit: (values: CreateCompanyPolicyDTO) => void;
          onCancel: () => void;
          isSubmitting?: boolean;
      }
    | {
          mode: 'edit';
          policy?: CompanyPolicyDetailDTO | null;
          onSubmit: (values: UpdateCompanyPolicyDTO) => void;
          onCancel: () => void;
          isSubmitting?: boolean;
      };

export function PolicyForm(props: PolicyFormProps) {
    const { mode, policy, onCancel, isSubmitting } = props;
    const isCreate = mode === 'create';
    const duplicateFrom =
        isCreate && 'duplicateFrom' in props ? props.duplicateFrom : null;
    const canManageTypes = usePermissions().has(
        Permissions.hr_company_policy.update,
    );
    const listModal = useDisclosure();
    const createModal = useDisclosure();
    const editModal = useDisclosure<PolicyType>();
    const listQuery = usePolicyTypesQuery();
    const policyTypes = listQuery.data ?? [];
    const createPolicyType = useCreatePolicyTypeMutation();
    const updatePolicyType = useUpdatePolicyTypeMutation();
    const deletePolicyType = useDeletePolicyTypeMutation();
    const [sampleIndex, setSampleIndex] = useState(0);
    const isDevMode =
        typeof import.meta !== 'undefined' &&
        (import.meta.hot !== undefined ||
            import.meta.env?.MODE === 'development');

    const policyTypeOptions = useMemo(
        () => [
            { value: POLICY_TYPE_NONE, label: '— ເລືອກປະເພດ —' },
            ...(listQuery.data ?? []).map((t) => ({
                value: t.id,
                label: t.name,
            })),
        ],
        [listQuery.data],
    );

    const defaultValues: PolicyFormValues = useMemo(() => {
        if (policy) {
            return {
                title: policy.title,
                code: policy.code ?? '',
                description: policy.description ?? '',
                policyTypeId: policy.policyTypeId ?? POLICY_TYPE_NONE,
                effectiveDate: policy.effectiveDate.slice(0, 10),
                content: policy.content ?? '',
            };
        }
        if (duplicateFrom) {
            return {
                title: `${duplicateFrom.title} (ສຳເນົາ)`,
                code: duplicateFrom.code ?? '',
                description: duplicateFrom.description ?? '',
                policyTypeId: duplicateFrom.policyTypeId ?? POLICY_TYPE_NONE,
                effectiveDate: duplicateFrom.effectiveDate.slice(0, 10),
                content: duplicateFrom.content ?? '',
            };
        }
        const today = new Date().toISOString().slice(0, 10);
        return {
            title: '',
            code: '',
            description: '',
            policyTypeId: POLICY_TYPE_NONE,
            effectiveDate: today,
            content: '',
        };
    }, [policy, duplicateFrom]);

    const methods = RHF.useForm<PolicyFormValues>({
        // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference
        resolver: zodResolver(PolicyFormSchema) as any,
        defaultValues,
        values:
            !isCreate && policy
                ? {
                      title: policy.title,
                      code: policy.code ?? '',
                      description: policy.description ?? '',
                      policyTypeId: policy.policyTypeId ?? POLICY_TYPE_NONE,
                      effectiveDate: policy.effectiveDate.slice(0, 10),
                      content: policy.content ?? '',
                  }
                : undefined,
    });

    const handleSubmitValues = (values: PolicyFormValues) => {
        const contentRaw =
            typeof values.content === 'string' ? values.content : '';
        const contentTrimmed = contentRaw.trim();
        const payload = {
            title: values.title.trim(),
            code: values.code.trim() || null,
            description: values.description.trim() || null,
            policyTypeId:
                values.policyTypeId === POLICY_TYPE_NONE ||
                !values.policyTypeId.trim()
                    ? null
                    : values.policyTypeId.trim(),
            effectiveDate: values.effectiveDate,
            content: contentTrimmed || null,
        };
        if (isCreate) {
            (props as PolicyFormProps & { mode: 'create' }).onSubmit(payload);
        } else {
            (props as PolicyFormProps & { mode: 'edit' }).onSubmit(payload);
        }
    };

    const handleAutoFillSample = () => {
        if (!isCreate || policyFormSamples.length === 0) return;
        const sample = policyFormSamples[sampleIndex];
        if (!sample) return;

        const matchedPolicyType = sample.policyTypeName
            ? policyTypes.find((item) => item.name === sample.policyTypeName)
            : undefined;

        methods.setValue('title', sample.title);
        methods.setValue('code', sample.code);
        methods.setValue('description', sample.description);
        methods.setValue('content', sample.content);
        methods.setValue(
            'effectiveDate',
            sample.effectiveDate ?? new Date().toISOString().slice(0, 10),
        );
        methods.setValue(
            'policyTypeId',
            matchedPolicyType?.id ?? POLICY_TYPE_NONE,
        );

        setSampleIndex((prev) => (prev + 1) % policyFormSamples.length);
        toast.success('Auto-filled form with sample data');
    };

    return (
        <FormRoot<PolicyFormValues>
            methods={methods}
            onSubmit={handleSubmitValues}
            className="space-y-6"
        >
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">ຂໍ້ມູນນະໂຍບາຍ</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <FormInput
                        name="title"
                        label="ຊື່ນະໂຍບາຍ"
                        requiredMark
                        placeholder="ຕົວຢ່າງ: ລະບຽບການລາພັກ"
                    />
                    <FormInput
                        name="code"
                        label="ລະຫັດ"
                        placeholder="ອີກເລືອກ"
                    />
                    <FormTextarea
                        name="description"
                        label="ຄຳອະທິບາຍ"
                        placeholder="ອະທິບາຍສັ້ນໆ"
                        rows={2}
                    />
                    <RHF.Controller
                        name="policyTypeId"
                        control={methods.control}
                        render={({ field }) => (
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <label
                                        className="font-medium text-sm"
                                        htmlFor="policy-form-policy-type"
                                    >
                                        ປະເພດ
                                    </label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <SimpleSelect
                                        id="policy-form-policy-type"
                                        options={policyTypeOptions}
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        placeholder="ເລືອກປະເພດ"
                                        className="w-full"
                                        aria-label="ປະເພດ"
                                    />
                                    {canManageTypes && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="icon"
                                            onClick={() => listModal.open()}
                                            title="ຈັດການປະເພດນະໂຍບາຍ"
                                        >
                                            <Settings className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    />

                    {/* List Policy Types Modal */}
                    <Modal
                        open={!!listModal.isOpen}
                        onOpenChange={(open) => {
                            if (!open) {
                                listModal.close();
                                createModal.close();
                                editModal.close();
                            }
                        }}
                        title="ຈັດການປະເພດນະໂຍບາຍ"
                        description="ສ້າງ, ແກ້ໄຂ, ຫຼື ລຶບປະເພດນະໂຍບາຍ"
                        size="md"
                    >
                        <div className="space-y-4">
                            <div className="flex justify-end">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        listModal.close();
                                        createModal.open();
                                    }}
                                >
                                    <PlusIcon className="mr-2 h-4 w-4" />
                                    ສ້າງປະເພດນະໂຍບາຍໃໝ່
                                </Button>
                            </div>

                            {listQuery.isLoading ? (
                                <div className="py-8 text-center text-muted-foreground">
                                    ກຳລັງໂຫຼດ...
                                </div>
                            ) : policyTypes.length === 0 ? (
                                <div className="py-8 text-center text-muted-foreground">
                                    ຍັງບໍ່ມີປະເພດນະໂຍບາຍ
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {policyTypes.map((type) => (
                                        <div
                                            key={type.id}
                                            className="flex items-center justify-between rounded-lg border p-3"
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="font-medium">
                                                    {type.name}
                                                </span>
                                                {type.description && (
                                                    <span className="text-muted-foreground text-xs">
                                                        {type.description}
                                                    </span>
                                                )}
                                                <span className="text-muted-foreground text-xs">
                                                    ລຳດັບ: {type.orderIndex}
                                                </span>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        listModal.close();
                                                        editModal.openWith(
                                                            type,
                                                        );
                                                    }}
                                                >
                                                    ແກ້ໄຂ
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={async () => {
                                                        if (
                                                            await confirm({
                                                                title: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບປະເພດນະໂຍບາຍ "${type.name}"?`,
                                                                description:
                                                                    'ນະໂຍບາຍທີ່ໃຊ້ປະເພດນີ້ຈະຖືກປ່ຽນເປັນບໍ່ມີປະເພດ.',
                                                                actionText:
                                                                    'ລຶບ',
                                                                ActionProps: {
                                                                    variant:
                                                                        'destructive',
                                                                },
                                                            })
                                                        ) {
                                                            await deletePolicyType.mutateAsync(
                                                                type.id,
                                                            );
                                                        }
                                                    }}
                                                >
                                                    ລຶບ
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Modal>

                    {/* Create Policy Type Modal */}
                    <Modal
                        open={!!createModal.isOpen}
                        onOpenChange={(open) => {
                            if (!open) createModal.close();
                            else listModal.close();
                        }}
                        title="ສ້າງປະເພດນະໂຍບາຍ"
                        description="ກຳນົດຊື່, ຄຳອະທິບາຍ, ແລະ ລຳດັບຂອງປະເພດນະໂຍບາຍ."
                        size="sm"
                    >
                        <PolicyTypeForm
                            onSubmit={async (vals) => {
                                await createPolicyType.mutateAsync({
                                    name: vals.name ?? '',
                                    description: vals.description ?? null,
                                    orderIndex: vals.orderIndex ?? 0,
                                });
                                createModal.close();
                                listModal.open();
                            }}
                            submitting={createPolicyType.isPending}
                        />
                    </Modal>

                    {/* Edit Policy Type Modal */}
                    <Modal
                        open={!!editModal.isOpen}
                        onOpenChange={(open) => {
                            if (!open) editModal.close();
                            else listModal.close();
                        }}
                        title="ແກ້ໄຂປະເພດນະໂຍບາຍ"
                        description="ປັບປຸງລາຍລະອຽດຂອງປະເພດນະໂຍບາຍ."
                        size="sm"
                    >
                        <PolicyTypeForm
                            initialValues={
                                editModal.data
                                    ? {
                                          name: editModal.data.name,
                                          description:
                                              editModal.data.description,
                                          orderIndex: editModal.data.orderIndex,
                                      }
                                    : undefined
                            }
                            onSubmit={async (vals) => {
                                if (!editModal.data) return;
                                await updatePolicyType.mutateAsync({
                                    id: editModal.data.id,
                                    dto: {
                                        name: vals.name ?? '',
                                        description: vals.description ?? null,
                                        orderIndex: vals.orderIndex ?? 0,
                                    },
                                });
                                editModal.close();
                                listModal.open();
                            }}
                            submitting={updatePolicyType.isPending}
                        />
                    </Modal>
                    <FormInput
                        name="effectiveDate"
                        label="ວັນທີມີຜົນບັງຄັບໃຊ້"
                        type="date"
                        requiredMark
                    />
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">ເນື້ອໃນ</CardTitle>
                    <p className="text-muted-foreground text-sm">
                        ເນື້ອໃນນະໂຍບາຍ ຫຼື ຄູ່ມືການປະຕິບັດ (ຂໍ້ຄວາມ)
                    </p>
                </CardHeader>
                <CardContent>
                    <FormTipTapEditor
                        name="content"
                        placeholder="ໃສ່ເນື້ອໃນນະໂຍບາຍ..."
                        className="font-mono text-sm"
                        toolbarElements={[
                            'bold',
                            'italic',
                            'strikethrough',
                            'bulletList',
                            'orderedList',
                            'blockquote',
                            'codeBlock',
                            'alignLeft',
                            'alignCenter',
                            'alignRight',
                            'alignJustify',
                            'undo',
                            'redo',
                        ]}
                    />
                </CardContent>
            </Card>

            <div className="sticky bottom-0 z-10 mt-8 flex flex-wrap items-center justify-between gap-4 border-t bg-background/95 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] backdrop-blur supports-backdrop-filter:bg-background/80">
                <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" onClick={onCancel}>
                        ຍົກເລີກ
                    </Button>
                    {isCreate && isDevMode && policyFormSamples.length > 0 && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleAutoFillSample}
                            disabled={isSubmitting}
                            className="gap-1.5"
                        >
                            <Sparkles className="h-4 w-4" />
                            ຕື່ມຕົວຢ່າງ ({sampleIndex + 1}/
                            {policyFormSamples.length})
                        </Button>
                    )}
                </div>
                <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting
                        ? 'ກຳລັງບັນທຶກ...'
                        : isCreate
                          ? 'ສ້າງນະໂຍບາຍ'
                          : 'ບັນທຶກການປ່ຽນແປງ'}
                </Button>
            </div>
        </FormRoot>
    );
}
